// Traductions côté React : même résultat que `t(...)` côté Rails.
//
// Les dictionnaires viennent de config/locales, exportés par
// `bin/rails frontend:i18n` (lib/frontend_translations.rb) : chaque langue y
// est déjà fusionnée par-dessus l'anglais, comme le fait config.i18n.fallbacks.
//
// Ce qui est reproduit de Rails (I18n::Backend::Simple + ActionView#translate) :
//   - `scope:` et clés pointées ; `default:` chaîne, clé (préfixée par ':' ou
//     passée avec { key }), ou liste essayée dans l'ordre ;
//   - pluriel du backend par défaut, sans règles CLDR : count == 0 et clé
//     `zero` présente → zero ; count == 1 → one ; sinon other ;
//   - interpolation `%{nom}` et `%%` (valeurs passées par String(), comme to_s) ;
//   - clés `_html` / `.html` : les valeurs interpolées sont échappées, sauf
//     celles marquées `safe(...)`, et le résultat est du HTML à insérer tel
//     quel (tHtml) ;
//   - clé absente : le dernier segment « titleizé » (Rails rend en plus un
//     <span class="translation_missing">, voir MissingTranslation).
//
// Pas de clés « paresseuses » (t('.titre')) : toujours la clé complète
// ('dashboard.stocks_table.titre').

type Messages = { [key: string]: unknown }

export type TranslateOptions = {
  scope?: string | string[]
  default?: unknown
  count?: number
  [variable: string]: unknown
}

const RESERVED = new Set([
  'scope', 'default', 'separator', 'resolve', 'object', 'fallback', 'fallback_in_progress',
  'fallback_original_locale', 'format', 'cascade', 'throw', 'raise', 'deep_interpolation',
  'exception_handler',
])

let currentLocale = 'en'
let currentMessages: Messages = {}

export function setTranslations(locale: string, messages: Messages): void {
  currentLocale = locale
  currentMessages = messages
}

export function locale(): string {
  return currentLocale
}

// Valeur HTML déjà sûre, à ne pas échapper dans une clé _html (html_safe).
const SAFE = Symbol('html_safe')
export type SafeHtml = { [SAFE]: true; html: string }
export function safe(html: string): SafeHtml {
  return { [SAFE]: true, html }
}
function isSafe(v: unknown): v is SafeHtml {
  return typeof v === 'object' && v !== null && (v as Record<symbol, unknown>)[SAFE] === true
}

export function escapeHtml(value: string): string {
  // ERB::Util.html_escape
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizeKeys(key: string, scope?: string | string[]): string[] {
  const scopeParts = scope === undefined ? [] : Array.isArray(scope) ? scope.flatMap((s) => String(s).split('.')) : String(scope).split('.')
  return [...scopeParts, ...key.split('.')].filter((p) => p !== '')
}

function lookup(keys: string[]): unknown {
  let node: unknown = currentMessages
  for (const k of keys) {
    if (node === null || typeof node !== 'object' || Array.isArray(node)) return undefined
    node = (node as Messages)[k]
    if (node === undefined) return undefined
  }
  return node
}

function rubyToS(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (isSafe(value)) return value.html
  return String(value)
}

// I18n::INTERPOLATION_PATTERN, sans le format %<nom>d (inutilisé dans l'app).
const INTERPOLATION = /%%|%\{([\w-]+)\}/g

function interpolate(text: string, vars: TranslateOptions, html: boolean, key: string): string {
  // I18n::Backend::Base#interpolate : sans variable, le texte reste tel quel,
  // `%%` compris.
  if (!Object.keys(vars).some((k) => !RESERVED.has(k))) return text
  return text.replace(INTERPOLATION, (match, name: string | undefined) => {
    if (match === '%%') return '%'
    if (name === undefined || RESERVED.has(name)) return match
    if (!(name in vars)) {
      if (import.meta.env.DEV) console.error(`[i18n] variable manquante %{${name}} pour « ${key} »`)
      return match
    }
    const value = vars[name]
    if (html && !isSafe(value) && !(name === 'count' && typeof value === 'number')) return escapeHtml(rubyToS(value))
    return rubyToS(value)
  })
}

function pluralize(entry: unknown, count: number | undefined): unknown {
  if (count === undefined || entry === null || typeof entry !== 'object' || Array.isArray(entry)) return entry
  const forms = entry as Messages
  const form = count === 0 && 'zero' in forms ? 'zero' : count === 1 ? 'one' : 'other'
  return forms[form]
}

function resolveDefault(def: unknown, options: TranslateOptions): unknown {
  const candidates = Array.isArray(def) ? def : [def]
  for (const c of candidates) {
    if (c === null || c === undefined) continue
    if (typeof c === 'object' && 'key' in (c as object)) {
      const v = lookup(normalizeKeys(String((c as { key: string }).key), options.scope))
      if (v !== undefined) return v
      continue
    }
    if (typeof c === 'string' && c.startsWith(':')) {
      const v = lookup(normalizeKeys(c.slice(1), options.scope))
      if (v !== undefined) return v
      continue
    }
    return c
  }
  return undefined
}

function titleize(word: string): string {
  // ActiveSupport::Inflector.titleize(humanize(underscore(word)))
  const humanized = word
    .replace(/([A-Z\d]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase()
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .trim()
  return humanized.replace(/\b(?<!\w['’`()])[a-z]/g, (c) => c.toUpperCase())
}

export const isHtmlKey = (key: string): boolean => /(?:_|\b)html$/.test(key)

type Translation = { value: unknown; missing: boolean; keys: string[] }

function translateRaw(key: string, options: TranslateOptions, html: boolean): Translation {
  const keys = normalizeKeys(key, options.scope)
  let entry = lookup(keys)
  if (entry === undefined && options.default !== undefined) entry = resolveDefault(options.default, options)
  entry = pluralize(entry, options.count)
  if (entry === undefined || entry === null) return { value: undefined, missing: true, keys }
  if (typeof entry === 'string') entry = interpolate(entry, options, html, key)
  return { value: entry, missing: false, keys }
}

/** `t('cle', { var: … })` de Rails, pour du texte. */
export function t(key: string, options: TranslateOptions = {}): string {
  const html = isHtmlKey(key)
  const r = translateRaw(key, options, html)
  if (r.missing) {
    if (import.meta.env.DEV) console.warn(`[i18n] traduction manquante : ${currentLocale}.${r.keys.join('.')}`)
    return titleize(r.keys[r.keys.length - 1] || key)
  }
  if (html && import.meta.env.DEV) console.warn(`[i18n] « ${key} » est une clé _html : utiliser tHtml()`)
  return typeof r.value === 'string' ? r.value : String(r.value)
}

/** Clé `_html` : HTML à insérer tel quel (dangerouslySetInnerHTML). */
export function tHtml(key: string, options: TranslateOptions = {}): string {
  const r = translateRaw(key, options, true)
  if (r.missing) {
    if (import.meta.env.DEV) console.warn(`[i18n] traduction manquante : ${currentLocale}.${r.keys.join('.')}`)
    const last = r.keys[r.keys.length - 1] || key
    return `<span class="translation_missing" title="translation missing: ${escapeHtml(`${currentLocale}.${r.keys.join('.')}`)}">${escapeHtml(titleize(last))}</span>`
  }
  return typeof r.value === 'string' ? r.value : String(r.value)
}

/** Valeur brute d'une clé : tableau, objet, nombre… (I18n.t sur un nœud). */
export function tRaw<T = unknown>(key: string, options: TranslateOptions = {}): T | undefined {
  const r = translateRaw(key, options, false)
  return r.missing ? undefined : (r.value as T)
}

/** La clé existe-t-elle (I18n.exists?) */
export function tExists(key: string, options: Pick<TranslateOptions, 'scope'> = {}): boolean {
  return lookup(normalizeKeys(key, options.scope)) !== undefined
}
